/**
 * Sentry beforeSend handler for client-side (browser) runtime.
 */

import type { ErrorEvent, EventHint } from '@sentry/nextjs';
import { sanitizeUserAgent } from '../validation';
import { sanitizeErrorForSentry } from './error-sanitizer';
import { THIRD_PARTY_DOMAINS } from './third-party-domains';

/**
 * Returns true when the error is a bare "Invalid data" thrown by a third-party
 * script (e.g. Crisp chat widget). Our own API client always wraps JSON parse
 * failures with additional context (status code, response preview, etc.), so a
 * bare "Invalid data" message is never produced by our code.
 */
function isBareInvalidDataFromThirdParty(event: ErrorEvent, hint: EventHint): boolean {
  const exception = hint?.originalException;

  // Extract the raw error message
  let message = '';
  if (exception instanceof Error) {
    message = exception.message;
  } else if (typeof exception === 'string') {
    message = exception;
  }

  // Only target the bare string — our API client always adds context
  if (message !== 'Invalid data') {
    return false;
  }

  // If we have a stack trace, check whether it comes from a third-party script
  const stack = exception instanceof Error ? exception.stack : undefined;
  if (stack && THIRD_PARTY_DOMAINS.some((domain) => stack.includes(domain))) {
    return true;
  }

  // Also check Sentry's structured frames for third-party origins
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (frames && frames.length > 0) {
    const hasOurCode = frames.some(
      (f) => f.filename && (f.filename.includes('/src/') || f.filename.includes('/_next/'))
    );
    if (!hasOurCode) {
      return true; // No frames from our code — almost certainly third-party
    }
  }

  // If there's no stack at all, a bare "Invalid data" with no context is noise
  if (!stack && (!frames || frames.length === 0)) {
    return true;
  }

  return false;
}

/**
 * Create a beforeSend handler for client-side (browser) runtime.
 * Handles error sanitization, third-party noise filtering, and adds browser context.
 */
export function createClientBeforeSend() {
  return (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
    try {
      // Drop bare "Invalid data" errors from third-party scripts (e.g. Crisp chat widget).
      // These are not actionable and were historically the #1 source of noise for this error.
      if (isBareInvalidDataFromThirdParty(event, hint)) {
        return null;
      }

      // Safely handle hint parameter - it may be undefined or have undefined properties
      if (hint?.originalException) {
        // Sanitize error if it has request/response properties
        // Use shared sanitization utility to ensure consistency and prevent module reference copying
        const error = hint.originalException;
        if (error && typeof error === 'object' && ('request' in error || 'response' in error)) {
          hint.originalException = sanitizeErrorForSentry(error);
        }
      }

      // Add browser context
      if (typeof window !== 'undefined' && event?.contexts) {
        const userAgent = sanitizeUserAgent(
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined
        );
        event.contexts.browser = {
          name: userAgent,
          version: userAgent,
        };
        event.contexts.device = {
          screen_width: window.screen?.width,
          screen_height: window.screen?.height,
        };
      }
      return event;
    } catch (error) {
      // If beforeSend itself fails, return the event as-is to prevent infinite loops
      // Use console.warn here since logger may not be available during initialization
      console.warn('Sentry beforeSend error:', error);
      return event;
    }
  };
}
